import { DatacentralHookForm } from '@/components/ui/form-field/datacentral-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    createServicePrice,
    updateServicePrice,
} from '@/modules/Service/services'
import type {
    CreateServicePricePayload,
    ServicePrice,
} from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'

export interface ServicePriceModalProps extends ModalProps {
    serviceID: number
    price?: ServicePrice
}

interface FormValues {
    price: number
    effective_from: string
    effective_to: string | null
    description: string
}

const ServicePriceModal: React.FC<ServicePriceModalProps> = ({
    open,
    onClose,
    serviceID,
    price,
}) => {
    const { t } = useTranslation('services')
    const { t: tCommon } = useTranslation()
    const isEdit = !!price
    const isRegister = price?.price_type === 'register'

    const formControl = useForm<FormValues>({
        defaultValues: {
            price: price?.price || 0,
            effective_from:
                price?.effective_from || dayjs().format('YYYY-MM-DD'),
            effective_to: price?.effective_to || null,
            description: price?.description || '',
        },
    })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_PRICES],
    })

    const { mutateAsync: createMutation, isPending: isCreating } =
        useMutationWithHandler(
            { mutationFn: createServicePrice },
            {
                successMessage: t('prices.create_success'),
                reactQueryKeys: QUERY_KEYS.SERVICES_PRICES,
                onSuccess: () => {
                    onClose()
                    refetch()
                },
            }
        )

    const { mutateAsync: updateMutation, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateServicePrice },
            {
                successMessage: t('prices.update_success'),
                reactQueryKeys: QUERY_KEYS.SERVICES_PRICES,
                onSuccess: () => {
                    onClose()
                    refetch()
                },
            }
        )

    const isPending = isCreating || isUpdating

    const onSubmit = (values: FormValues) => {
        const payload: CreateServicePricePayload = {
            price_type: price?.price_type || 'recurring',
            price: Number(values.price),
            effective_from: values.effective_from,
            effective_to: values.effective_to || undefined,
            description: values.description || undefined,
        }

        if (isEdit && price) {
            updateMutation({
                serviceID,
                priceID: price.id,
                data: {
                    price: payload.price,
                    effective_from: payload.effective_from,
                    effective_to: payload.effective_to ?? undefined,
                    description: payload.description,
                },
            })
        } else {
            createMutation({ serviceID, data: payload })
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                isRegister
                    ? t('prices.edit_register', {
                          defaultValue: 'Chỉnh sửa giá bán mới',
                      })
                    : t('prices.request_price_change', {
                          defaultValue: 'Yêu cầu thay đổi giá gia hạn dịch vụ',
                      })
            }
        >
            <DatacentralHookForm<FormValues>
                formControl={formControl}
                onSubmit={onSubmit}
                columns={2}
                data={[
                    {
                        formItemProps: {
                            name: 'price',
                            label: t('prices.price'),
                            type: 'number',
                            required: true,
                        },
                        colSpan: 2,
                    },
                    {
                        formItemProps: {
                            name: 'effective_from',
                            label: t('prices.effective_from'),
                            type: 'datepicker',
                            disabled: isRegister,
                            required: !isRegister,
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'effective_to',
                            label: t('prices.effective_to'),
                            type: 'datepicker',
                            disabled: isRegister,
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'description',
                            label: t('prices.description'),
                            type: 'textarea',
                        },
                        colSpan: 2,
                    },
                ]}
                actions={[
                    {
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        title: tCommon('cancel'),
                    },
                    {
                        type: 'submit',
                        variant: 'default',
                        title: tCommon('save'),
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
            />
        </Dialog>
    )
}

export default ServicePriceModal
